import React from 'react';

import { Badge } from '@/components/ui/badge';
import { Time } from '@/components/ui/time';
import type { SiteEvent } from '@/hooks/useSiteEvents';
import {
  getDescriptor,
  statusBadgeFor,
  type IconTone,
} from './eventDescriptors';

/**
 * GitHub-Conversation-style activity timeline.
 *
 * Layout: a single vertical rail behind the icon dots. Dots are neutral
 * by default — colored icons are reserved for genuine "moments" (e.g.
 * site creation). Status (Error / In progress) is conveyed by a small
 * badge on the right edge so the timeline reads cleanly at a glance and
 * problem rows stand out.
 *
 * Accessibility: real `<ol>` so screen readers see an ordered list; the
 * rail and dot are presentational only.
 */

// Inner tint layer for the icon. Translucent so it reads as a tinted
// chip — but it sits on top of an opaque `bg-surface` outer wrapper
// (rendered separately below) which is what actually covers the rail
// line. Result: bar is hidden behind the icon, AND the icon container
// keeps its translucent tertiary tint.
const ICON_TONE: Record<IconTone, string> = {
  neutral: 'bg-foreground/5 text-muted-foreground',
  success: 'bg-success/15 text-success',
};

/** Day key (locale-stable) for grouping events by calendar day. */
const dayKey = (iso: string): string => new Date(iso).toDateString();

interface EventTimelineProps {
  events: SiteEvent[];
}

export const EventTimeline: React.FC<EventTimelineProps> = ({ events }) => {
  // Resolve "is this the first event of its calendar day?" so the
  // date label only renders once per day. Day key is locale-stable
  // via `toDateString()`.
  let lastDay: string | null = null;
  const rows = events.map((e) => {
    const day = dayKey(e.createdAt);
    const showDate = day !== lastDay;
    lastDay = day;
    return { event: e, showDate };
  });

  return (
    <ol>
      {rows.map(({ event: e, showDate }, idx) => {
        const { Icon, verb, iconTone } = getDescriptor(e);
        const tone = ICON_TONE[iconTone ?? 'neutral'];
        const badge = statusBadgeFor(e);
        const isFirst = idx === 0;
        const isLast = idx === rows.length - 1;

        return (
          <li key={e.id} className="relative flex items-start gap-3 pb-5 last:pb-0">
            {/* Date column. `compact` format ("Sat May 02, 2026") is
                fixed 16-char by construction. Column has a stable
                min-width so rows without a date (events 2..n on the
                same day) hold the icon column in place. */}
            <div className="flex-shrink-0 min-w-36 text-left text-sm text-muted-foreground pt-1.5 tabular-nums whitespace-nowrap">
              {showDate && <Time value={e.createdAt} format="compact" />}
            </div>
            {/* Icon column: rail + dot. Rail is a 1px line through the
                center of this column, spanning the row's full height —
                the first row's segment starts at the icon, the last
                row's ends at it, every middle row goes edge-to-edge.
                Decoupling the rail from the date column means the date
                column can auto-size without breaking rail alignment. */}
            <div className="relative z-10 flex-shrink-0 h-8 w-8">
              <div
                aria-hidden="true"
                className={`absolute left-1/2 -translate-x-1/2 w-px bg-foreground/15 ${
                  isFirst ? 'top-4' : '-top-5'
                } ${isLast ? 'bottom-4' : 'bottom-0'}`}
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-full bg-background"
              />
              <div
                className={`relative h-8 w-8 rounded-full flex items-center justify-center ${tone}`}
              >
                <Icon className="h-4 w-4 stroke-[1.75]" />
              </div>
            </div>
            {/* Single inline sentence: actor + verb + relative-time, with
                an optional status badge to the right. All `text-sm` so
                the line reads as one continuous string. Time gets a
                tooltip showing the full ISO timestamp. */}
            <p className="flex-1 min-w-0 pt-1.5 text-sm text-foreground">
              <span className="font-medium">{e.actorLabel}</span>
              {' '}
              {verb(e)}
              {' '}
              <Time
                value={e.createdAt}
                format="relative"
                className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 underline-offset-4"
              />
              {badge && (
                // `normal-case` overrides the Badge primitive's `capitalize`
                // (which would title-case "In progress" → "In Progress").
                // Multi-word status labels here are pre-formatted as
                // sentence case and shouldn't be CSS-transformed.
                <Badge
                  variant={badge.variant}
                  className="text-xs px-2 py-0.5 normal-case ml-2 align-middle"
                >
                  {badge.label}
                </Badge>
              )}
            </p>
          </li>
        );
      })}
    </ol>
  );
};

export default EventTimeline;
